/**
 * Pure parsing + shaping helpers for the `lake_search_documents_by_name` tool
 * result. No React, no IO — the controller (LakeSearchDocumentsByNameToolUI)
 * and the presentational view both consume these.
 */

export interface LakeSearchDocumentsByNameArgs {
  name: string
  top_k?: number
}

export interface LakeDocumentByNameHit {
  document_id: string
  filename: string | null
  label: string | null
  storage_path: string | null
  file_type: string | null
  document_type: string | null
  snippet: string | null
  score: number
}

export interface LakeSearchDocumentsByNameResult {
  documents: LakeDocumentByNameHit[]
  total: number
  error?: string
}

/**
 * Backend payload arrives as a JSON string (or a pre-parsed object on history
 * replay). A non-JSON string is surfaced as an error result.
 */
export function parseResult(
  raw: LakeSearchDocumentsByNameResult | string | undefined
): LakeSearchDocumentsByNameResult | null {
  if (raw == null) return null
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as LakeSearchDocumentsByNameResult
    } catch {
      return { documents: [], total: 0, error: raw }
    }
  }
  return raw
}

/**
 * Lake filenames are stored prefixed with a content hash
 * (`<hash>_<original-name>`); strip the prefix for display, falling back to the
 * raw filename (or a generic label when absent).
 */
export function normalizeDisplayFilename(filename: string | null | undefined): string {
  if (!filename) return 'Document'
  const m = filename.match(/^([a-f0-9-]{12,})_(.+)$/i)
  return m?.[2] ?? filename
}
